import { ListItemCard } from '@monorepo/shared/componentsV2/ListItemCard';
import {
  selectWidth,
  StyledFormGroup,
  StyledSelect,
} from '@monorepo/shared/componentsV2/styled/form';
import { bodyMedium } from '@monorepo/shared/styles/text';
import { isRequired } from '@monorepo/shared/utils/validators';
import {
  DatasetPropertyType,
  DatasetResponse,
  DatetimeOriginTransformation,
  datetimeTransformationLabels,
} from 'mapistry-shared';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { getTimeSeriesJoinType, isDateConditionDisabled } from './helpers';
import { StyledConditionsContainer } from './styled';

function getTimeSeriesPropertyLabel(dataset: DatasetResponse) {
  const timeSeriesProperty = dataset.schemaProperties.find(
    (p) => p.isTimeSeriesDate,
  );
  return timeSeriesProperty?.type === DatasetPropertyType.DATE_RANGE
    ? 'Period'
    : 'Log Date';
}

const StyledTimeSeriesProperty = styled(ListItemCard)`
  width: ${selectWidth};
  margin-top: 0.125rem;
`;

const StyledLabel = styled.div`
  ${bodyMedium}
  color: ${({ theme }) => theme.colors.gray333};
`;

const StylesEqualText = styled.div`
  margin-top: 1.7rem;
`;

interface DateIntervalConditionProps {
  className?: string;
  leftDataset: DatasetResponse;
  rightDataset: DatasetResponse;
}

export function DateIntervalCondition({
  className,
  leftDataset,
  rightDataset,
}: DateIntervalConditionProps) {
  const leftTimeSeriesPropertyLabel = useMemo(
    () => getTimeSeriesPropertyLabel(leftDataset),
    [leftDataset],
  );
  const rightTimeSeriesPropertyLabel = useMemo(
    () => getTimeSeriesPropertyLabel(rightDataset),
    [rightDataset],
  );
  const isDateIntervalDisabled = useMemo(
    () =>
      isDateConditionDisabled(getTimeSeriesJoinType(leftDataset, rightDataset)),
    [leftDataset, rightDataset],
  );
  const tooltipText = useMemo(
    () =>
      isDateIntervalDisabled
        ? `When connecting a previously aggregated data source, the interval of the aggregation is required. You can match with additional fields below.`
        : `For the data source type you chose, you must choose a date interval to match on the Log Date fields. You can match with additional fields below.`,
    [isDateIntervalDisabled],
  );
  const options = useMemo(
    () =>
      Object.values(DatetimeOriginTransformation).map((transformation) => ({
        label: datetimeTransformationLabels[transformation],
        value: transformation,
      })),
    [],
  );

  return (
    <StyledConditionsContainer className={className}>
      <StyledFormGroup row>
        <div>
          <StyledLabel>Field Name</StyledLabel>
          <StyledTimeSeriesProperty
            content={leftTimeSeriesPropertyLabel}
            isLocked
          />
        </div>
        <StylesEqualText>equals</StylesEqualText>
        <div>
          <StyledLabel>Field Name</StyledLabel>
          <StyledTimeSeriesProperty
            content={rightTimeSeriesPropertyLabel}
            isLocked
          />
        </div>
      </StyledFormGroup>
      <StyledSelect
        label="Date Interval"
        disabled={isDateIntervalDisabled}
        options={options}
        name="dateInterval"
        required
        tooltipText={tooltipText}
        validate={isRequired}
      />
    </StyledConditionsContainer>
  );
}
